<app-page-breadcrumb pageTitle="Monedas" />

<div
  class="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 shadow-sm"
>
  <!-- Tab navigation -->
  <div class="border-b border-gray-200 dark:border-gray-700 px-6 pt-4">
    <nav class="-mb-px flex gap-1 overflow-x-auto">
      @for (
        tab of [
          { key: 'monedas', label: 'Monedas' },
          { key: 'cambio', label: 'Tipo de Cambio' },
        ];
        track tab.key
      ) {
        <button
          (click)="onTabChange(tab.key)"
          class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
          [class.border-brand-500]="activeTab === tab.key"
          [class.text-brand-600]="activeTab === tab.key"
          [class.dark:text-brand-400]="activeTab === tab.key"
          [class.border-transparent]="activeTab !== tab.key"
          [class.text-gray-500]="activeTab !== tab.key"
          [class.hover:text-gray-700]="activeTab !== tab.key"
          [class.dark:text-gray-400]="activeTab !== tab.key"
        >
          {{ tab.label }}
        </button>
      }
    </nav>
  </div>

  <div class="p-6">
    <!-- ===================== TAB 1: MONEDAS ===================== -->
    @if (activeTab === 'monedas') {
      <!-- Header -->
      <div class="mb-4 flex items-center justify-between">
        <h2 class="text-lg font-semibold text-gray-800 dark:text-white">
          Monedas
        </h2>
        <button
          (click)="openCreateCurrency()"
          class="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 transition-colors"
        >
          <svg
            class="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Nueva Moneda
        </button>
      </div>

      <!-- Loading -->
      @if (loadingCurrencies) {
        <div class="flex items-center justify-center py-12">
          <div
            class="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-500"
          ></div>
          <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando...</span>
        </div>
      }

      <!-- Error -->
      @if (errorCurrencies) {
        <div
          class="mb-4 rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-500/20 dark:bg-error-500/10"
        >
          <p class="text-sm text-error-600 dark:text-error-400">
            {{ errorCurrencies }}
          </p>
        </div>
      }

      <!-- Table -->
      @if (!loadingCurrencies && !errorCurrencies) {
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr
                class="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400"
              >
                <th class="py-3 px-4">Moneda</th>
                <th class="py-3 px-4">Código</th>
                <th class="py-3 px-4">Prefijo</th>
                <th class="py-3 px-4">País</th>
                <th class="py-3 px-4">Tasa USD</th>
                <th class="py-3 px-4">Actualizado</th>
                <th class="py-3 px-4">Fuente</th>
                <th class="py-3 px-4 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
              @for (c of currencies; track c.id) {
                <tr
                  class="transition hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <td
                    class="py-3 px-4 font-medium text-gray-800 dark:text-white"
                  >
                    {{ c.name }}
                  </td>
                  <td class="py-3 px-4 text-gray-600 dark:text-gray-300">
                    {{ c.code }}
                  </td>
                  <td class="py-3 px-4 text-gray-600 dark:text-gray-300">
                    {{ c.prefix }}
                  </td>
                  <td class="py-3 px-4 text-gray-500 dark:text-gray-400">
                    {{ getCountryName(c) }}
                  </td>
                  <td class="py-3 px-4">
                    <div class="flex items-center gap-2">
                      <span class="text-gray-700 dark:text-gray-300">
                        {{ c.rateUsd != null ? c.rateUsd : '—' }}
                      </span>
                      @if (isRateStale(c.rateUpdatedAt)) {
                        <span
                          class="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-600 dark:bg-amber-500/10 dark:text-amber-400"
                        >
                          Desactualizada
                        </span>
                      }
                    </div>
                  </td>
                  <td class="py-3 px-4 text-gray-500 dark:text-gray-400">
                    {{
                      c.rateUpdatedAt
                        ? (c.rateUpdatedAt | date: 'dd/MM/yyyy HH:mm')
                        : '—'
                    }}
                  </td>
                  <td class="py-3 px-4 text-gray-500 dark:text-gray-400">
                    {{ c.rateSource ?? '—' }}
                  </td>
                  <td class="py-3 px-4">
                    <div class="flex items-center justify-center gap-2">
                      <button
                        (click)="openRateModal(c)"
                        title="Actualizar tasa"
                        class="rounded-lg bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-600 hover:bg-brand-100 dark:bg-brand-500/10 dark:text-brand-400 dark:hover:bg-brand-500/20 transition-colors"
                      >
                        Tasa
                      </button>
                      <button
                        (click)="openEditCurrency(c)"
                        title="Editar"
                        class="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition-colors"
                      >
                        <svg
                          class="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        (click)="deleteCurrency(c)"
                        title="Eliminar"
                        class="rounded-lg p-1.5 text-gray-500 hover:bg-error-50 hover:text-error-600 dark:hover:bg-error-500/10 dark:hover:text-error-400 transition-colors"
                      >
                        <svg
                          class="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td
                    colspan="8"
                    class="py-10 text-center text-gray-400 dark:text-gray-500"
                  >
                    No hay monedas registradas.
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    }

    <!-- ===================== TAB 2: TIPO DE CAMBIO ===================== -->
    @if (activeTab === 'cambio') {
      <div class="mx-auto max-w-lg space-y-6">
        <h2 class="text-lg font-semibold text-gray-800 dark:text-white">
          Consultar Tipo de Cambio
        </h2>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >De</label
            >
            <select
              [(ngModel)]="exchangeForm.fromCurrencyCode"
              class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
              <option value="">-- Selecciona --</option>
              @for (c of currencies; track c.id) {
                <option [value]="c.code">{{ c.code }} — {{ c.name }}</option>
              }
            </select>
          </div>
          <div>
            <label
              class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >A</label
            >
            <select
              [(ngModel)]="exchangeForm.toCurrencyCode"
              class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
              <option value="">-- Selecciona --</option>
              @for (c of currencies; track c.id) {
                <option [value]="c.code">{{ c.code }} — {{ c.name }}</option>
              }
            </select>
          </div>
          <div class="col-span-2">
            <label
              class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Cantidad</label
            >
            <input
              [(ngModel)]="exchangeForm.amount"
              type="number"
              placeholder="1"
              min="1"
              (keydown.minus)="$event.preventDefault()"
              class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>
        </div>

        <button
          (click)="submitExchange()"
          [disabled]="
            loadingExchange ||
            !exchangeForm.fromCurrencyCode ||
            !exchangeForm.toCurrencyCode ||
            exchangeForm.amount <= 0
          "
          class="w-full rounded-lg bg-brand-500 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 transition-colors"
        >
          @if (loadingExchange) {
            Consultando...
          } @else {
            Consultar
          }
        </button>

        @if (errorExchange) {
          <div
            class="rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-500/20 dark:bg-error-500/10"
          >
            <p class="text-sm text-error-600 dark:text-error-400">
              {{ errorExchange }}
            </p>
          </div>
        }

        @if (exchangeResult) {
          <div
            class="rounded-xl border border-brand-200 bg-brand-50 p-5 dark:border-brand-500/20 dark:bg-brand-500/10"
          >
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {{ exchangeResult.fromCurrency }} →
              {{ exchangeResult.toCurrency }}
            </p>
            <p
              class="mt-1 text-3xl font-bold text-brand-600 dark:text-brand-400"
            >
              {{ exchangeResult.convertedAmount }}
            </p>
            @if (exchangeResult.timestamp) {
              <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">
                Actualizado:
                {{ exchangeResult.timestamp | date: 'dd/MM/yyyy HH:mm' }}
              </p>
            }
          </div>
        }
      </div>
    }
  </div>
</div>

<!-- ===================== MODAL: MONEDA ===================== -->
@if (showCurrencyModal) {
  <div
    class="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 p-4"
  >
    <div
      class="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900"
    >
      <h3 class="mb-5 text-lg font-semibold text-gray-800 dark:text-white">
        {{ editingCurrency ? 'Editar Moneda' : 'Nueva Moneda' }}
      </h3>

      <div class="space-y-4">
        <div>
          <label
            class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >País</label
          >
          <select
            [(ngModel)]="currencyForm.intIdCountry"
            (ngModelChange)="onCountryChange($event)"
            class="w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:bg-gray-800 dark:text-white"
            [class.border-gray-300]="!currencyErrors.intIdCountry"
            [class.dark:border-gray-600]="!currencyErrors.intIdCountry"
            [class.border-error-500]="currencyErrors.intIdCountry"
          >
            <option [ngValue]="0">-- Selecciona un país --</option>
            @for (country of countries; track country.id) {
              <option [ngValue]="country.id">{{ country.name }}</option>
            }
          </select>
          @if (currencyErrors.intIdCountry) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">
              {{ currencyErrors.intIdCountry }}
            </p>
          }
        </div>

        <div>
          <label
            class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >Nombre</label
          >
          <input
            type="text"
            [(ngModel)]="currencyForm.name"
            (ngModelChange)="clearCurrencyError('name')"
            placeholder="Ej: Dólar Estadounidense"
            class="w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
            [class.border-gray-300]="!currencyErrors.name"
            [class.dark:border-gray-600]="!currencyErrors.name"
            [class.border-error-500]="currencyErrors.name"
          />
          @if (currencyErrors.name) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">
              {{ currencyErrors.name }}
            </p>
          }
        </div>

        <div>
          <label
            class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Código
            <span class="ml-1 text-xs text-gray-400">(ISO 4217, ej: USD)</span>
          </label>
          <input
            type="text"
            [(ngModel)]="currencyForm.code"
            (ngModelChange)="clearCurrencyError('code')"
            placeholder="USD"
            maxlength="10"
            class="w-full rounded-lg border bg-white px-3 py-2 text-sm uppercase text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
            [class.border-gray-300]="!currencyErrors.code"
            [class.dark:border-gray-600]="!currencyErrors.code"
            [class.border-error-500]="currencyErrors.code"
          />
          @if (currencyErrors.code) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">
              {{ currencyErrors.code }}
            </p>
          }
        </div>

        <div>
          <label
            class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >Prefijo / Símbolo</label
          >
          <input
            type="text"
            [(ngModel)]="currencyForm.prefix"
            (ngModelChange)="clearCurrencyError('prefix')"
            placeholder="Ej: $"
            maxlength="10"
            class="w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
            [class.border-gray-300]="!currencyErrors.prefix"
            [class.dark:border-gray-600]="!currencyErrors.prefix"
            [class.border-error-500]="currencyErrors.prefix"
          />
          @if (currencyErrors.prefix) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">
              {{ currencyErrors.prefix }}
            </p>
          }
        </div>
      </div>

      <div class="mt-6 flex justify-end gap-3">
        <button
          (click)="showCurrencyModal = false"
          class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors"
        >
          Cancelar
        </button>
        <button
          (click)="saveCurrency()"
          [disabled]="savingCurrency"
          class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 transition-colors"
        >
          {{ savingCurrency ? 'Guardando...' : 'Guardar' }}
        </button>
      </div>
    </div>
  </div>
}

<!-- ===================== MODAL: TASA ===================== -->
@if (showRateModal) {
  <div
    class="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 p-4"
  >
    <div
      class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900"
    >
      <h3 class="mb-5 text-lg font-semibold text-gray-800 dark:text-white">
        Actualizar Tasa — {{ rateTargetCurrency?.name }}
      </h3>

      <div>
        <label
          class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >Tasa USD</label
        >
        <input
          type="number"
          step="any"
          [(ngModel)]="rateForm.rateUsd"
          placeholder="0.00"
          class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
        />
        <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
          Cuántas unidades de {{ rateTargetCurrency?.code }} equivalen a 1 USD.
        </p>
      </div>

      <div class="mt-6 flex justify-end gap-3">
        <button
          (click)="showRateModal = false"
          class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors"
        >
          Cancelar
        </button>
        <button
          (click)="saveRate()"
          [disabled]="savingRate"
          class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 transition-colors"
        >
          {{ savingRate ? 'Guardando...' : 'Guardar' }}
        </button>
      </div>
    </div>
  </div>
}
